import React, { useEffect, useState } from 'react';
import styles from './style.module.scss';

const IGNORED_KEYS = new Set(['_id', '__v', 'createdAt', 'updatedAt', 'created_at', 'updated_at', 'po']);

const deepSearchSingleWord = (obj, word) => {
    if (typeof obj === 'string' || typeof obj === 'number') {
        return obj.toString().toLowerCase().includes(word);
    }

    if (Array.isArray(obj)) {
        return obj.some(item => deepSearchSingleWord(item, word));
    }

    if (typeof obj === 'object' && obj !== null) {
        return Object.entries(obj).some(([key, value]) => {
            if (IGNORED_KEYS.has(key)) {
                return false;
            }
            return deepSearchSingleWord(value, word);
        });
    }

    return false;
};

const deepSearch = (obj, searchTerm) => {
    if (!searchTerm) return true;
    
    // Parse search terms supporting quoted phrases
    const regex = /"([^"]+)"|'([^']+)'|(\S+)/g;
    const words = [];
    let match;
    while ((match = regex.exec(searchTerm)) !== null) {
        const token = match[1] || match[2] || match[3];
        if (token) {
            words.push(token.toLowerCase());
        }
    }

    if (words.length === 0) return true;
    return words.every(word => deepSearchSingleWord(obj, word));
};

function SearchBar({ searchKey = 'name', suggestionsSource = [], onSearchChange, customFilter }) {

    const [query, setQuery] = useState('');
    const [sortOption, setSortOption] = useState('created_desc');

    const sortOptions = [
        { label: 'Date Created: New to Old', value: 'created_desc' },
        { label: 'Date Created: Old to New', value: 'created_asc' },
        { label: 'Name/Title: A to Z', value: 'name_asc' },
        { label: 'Name/Title: Z to A', value: 'name_desc' }
    ];

    useEffect(() => {
        let filtered = [...(suggestionsSource || [])];

        if (query) {
            if (customFilter) {
                filtered = filtered.filter((item) => customFilter(item, query));
            } else {
                filtered = filtered.filter((item) => deepSearch(item, query));
            }
        }

        switch (sortOption) {
            case 'created_desc':
                filtered.sort((a, b) => new Date(b.created_at || b.updatedAt || 0) - new Date(a.created_at || a.updatedAt || 0));
                break;
            case 'created_asc':
                filtered.sort((a, b) => new Date(a.created_at || a.updatedAt || 0) - new Date(b.created_at || b.updatedAt || 0));
                break;
            case 'name_asc':
                filtered.sort((a, b) => (a.name || a.title || '').localeCompare(b.name || b.title || ''));
                break;
            case 'name_desc':
                filtered.sort((a, b) => (b.name || b.title || '').localeCompare(a.name || a.title || ''));
                break;
        }

        onSearchChange(filtered);
    }, [query, suggestionsSource, sortOption, searchKey, onSearchChange, customFilter]);

    return (
        <div className={styles.search_view}>
            <div className={styles.search_input_wrapper}>
                <input
                    type="text"
                    placeholder="Search..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className={styles.search_input}
                />
                <span className={styles.search_icon_button} type="button">
                    🔍
                </span>
            </div>

            <select
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className={styles.sort_dropdown}
            >
                {sortOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

export default SearchBar;
